<template>
  <div class="material-kanban">
    <div class="kanban-container">
      <!-- Columna Preparación -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'preparacion')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header preparacion">
          <h5 class="mb-0">
            <i class="fas fa-tools text-orange mr-2"></i>
            Preparación
          </h5>
          <span class="kanban-count preparacion">{{ getMaterialesByEstado('preparacion').length }}</span>
        </div>
        <div class="column-content">
          <div v-for="material in getMaterialesByEstado('preparacion')" 
            :key="material.id"
            class="material-card"
            :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
            draggable="true"
            @dragstart="onDragStart($event, material)"
            @dragend="onDragEnd"
            @click="handleCardClick($event, material)"
            
            style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <!-- Para Sacas -->
                  <div v-if="material.es_saca || material.tipo_material === 'saca'">
                    <h6 class="material-title mb-1">
                      <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                    </h6>
                    <p class="material-description mb-2">
                      <strong>QR:</strong> {{ material.qr || '-' }}
                    </p>
                  </div>
                  <!-- Para Activos -->
                  <div v-else-if="material.activo">
                    <h6 class="material-title mb-1">
                      <strong>{{ material.activo.nombre }}</strong>
                    </h6>
                    <p v-if="material.activo.descripcion" class="material-description mb-2">
                      {{ material.activo.descripcion }}
                    </p>
                  </div>
                  <!-- Para Consumibles -->
                  <div v-else>
                    <h6 class="material-title mb-1">
                      <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                    </h6>
                    <p class="material-quantity mb-2">
                      <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                    </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botón para ver incidencia (si existe) -->
              <div v-if="hasActiveIncidence(material)" class="mt-2">
                <button class="btn btn-incidence-enhanced btn-sm w-100" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
              </div>
              
              <button class="btn btn-primary btn-sm w-100" 
                      @click.stop="asignarJefeEquipo(material)"
                      :disabled="loading">
                <i class="fas fa-user-plus"></i> Asignar Jefe Equipo
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Asignado -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'asignado')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header asignado">
          <h5 class="mb-0">
            <i class="fas fa-user-check text-blue mr-2"></i>
            Asignado
          </h5>
          <span class="kanban-count asignado">{{ getMaterialesByEstado('asignado').length + getMaterialesByEstado('rechazado').length }}</span>
        </div>
        <div class="column-content">
          <!-- Materiales Asignados -->
          <div v-for="material in getMaterialesByEstado('asignado')" 
               :key="material.id"
               class="material-card"
               :class="[
                 material.activo ? 'material-card-activo' : 'material-card-consumible',
                 { 'material-card-con-incidencia': hasActiveIncidence(material) }
               ]"
               draggable="true"
               @dragstart="onDragStart($event, material)"
               @dragend="onDragEnd"
               @click="handleCardClick($event, material)"
               
               style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <!-- Para Sacas -->
                  <div v-if="material.es_saca || material.tipo_material === 'saca'">
                    <h6 class="material-title mb-1">
                      <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                    </h6>
                    <p class="material-description mb-2">
                      <strong>QR:</strong> {{ material.qr || '-' }}
                    </p>
                  </div>
                  <!-- Para Activos -->
                  <div v-else-if="material.activo">
                    <h6 class="material-title mb-1">
                      <strong>{{ material.activo.nombre }}</strong>
                    </h6>
                    <p v-if="material.activo.descripcion" class="material-description mb-2">
                      {{ material.activo.descripcion }}
                    </p>
                  </div>
                  <!-- Para Consumibles -->
                  <div v-else>
                    <h6 class="material-title mb-1">
                      <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                    </h6>
                    <p class="material-quantity mb-2">
                      <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                    </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botón para ver incidencia (si existe) -->
              <div v-if="hasActiveIncidence(material)" class="mt-2">
                <button class="btn btn-incidence-enhanced btn-sm w-100" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
              </div>
            </div>
            <div class="card-footer">
              <span class="text-muted">
                <i class="fas fa-clock text-warning"></i>
                Pendiente aceptación
              </span>
            </div>
          </div>

          <!-- Materiales Rechazados -->
          <div v-for="material in getMaterialesByEstado('rechazado')" 
               :key="'rechazado-' + material.id"
               class="material-card material-card-rechazado"
               :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
               draggable="true"
               @dragstart="onDragStart($event, material)"
               @dragend="onDragEnd"
               @click="handleCardClick($event, material)"
               
               style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
              <!-- Para Sacas -->
              <div v-if="material.es_saca || material.tipo_material === 'saca'">
                <h6 class="material-title mb-1">
                  <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                </h6>
                <p class="material-description mb-2">
                  <strong>QR:</strong> {{ material.qr || '-' }}
                </p>
              </div>
              <!-- Para Activos -->
              <div v-else-if="material.activo">
                <h6 class="material-title mb-1">
                  <strong>{{ material.activo.nombre }}</strong>
              </h6>
                <p v-if="material.activo.descripcion" class="material-description mb-2">
                  {{ material.activo.descripcion }}
                </p>
              </div>
              <!-- Para Consumibles -->
              <div v-else>
                <h6 class="material-title mb-1">
                  <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                </h6>
                <p class="material-quantity mb-2">
                  <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botón para ver incidencia (si existe) -->
              <div v-if="hasActiveIncidence(material)" class="mt-2">
                <button class="btn btn-incidence-enhanced btn-sm w-100" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
              </div>
            </div>
            <div class="card-footer">
              <div class="d-flex flex-column">
                <span class="text-muted mb-1">
                  <i class="fas fa-times-circle text-danger"></i>
                  Material rechazado
                </span>
                <!-- Mostrar motivo del rechazo si existe -->
                <small v-if="material.observaciones" class="text-muted" style="font-size: 0.75rem;">
                  <i class="fas fa-info-circle mr-1"></i>
                  <strong>Motivo:</strong> {{ extraerMotivoRechazo(material.observaciones) }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Entregado -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'entregado')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header entregado">
          <h5 class="mb-0">
            <i class="fas fa-check-circle text-warning mr-2"></i>
            Entregado
          </h5>
          <span class="kanban-count entregado">{{ getMaterialesByEstado('entregado').length }}</span>
        </div>
        <div class="column-content">
          <div v-for="material in getMaterialesByEstado('entregado')" 
            :key="material.id"
            class="material-card"
               :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
               draggable="true"
               @dragstart="onDragStart($event, material)"
               @dragend="onDragEnd"
               @click="handleCardClick($event, material)"
           
            style="cursor: pointer;">
            <!-- Debug: Mostrar datos del material -->
            
            
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <!-- Para Sacas -->
                  <div v-if="material.es_saca || material.tipo_material === 'saca'">
                    <h6 class="material-title mb-1">
                      <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                    </h6>
                    <p class="material-description mb-2">
                      <strong>QR:</strong> {{ material.qr || '-' }}
                    </p>
                  </div>
                  <!-- Para Activos -->
                  <div v-else-if="material.activo">
                    <h6 class="material-title mb-1">
                      <strong>{{ material.activo.nombre }}</strong>
                    </h6>
                    <p v-if="material.activo.descripcion" class="material-description mb-2">
                      {{ material.activo.descripcion }}
                    </p>
                  </div>
                  <!-- Para Consumibles -->
                  <div v-else>
                    <h6 class="material-title mb-1">
                      <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                    </h6>
                    <p class="material-quantity mb-2">
                      <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                    </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
            </div>
            
            <!-- Botón para ver incidencia (si existe) -->
            <div v-if="hasActiveIncidence(material)" class="mt-2">
              <button class="btn btn-incidence-enhanced btn-sm w-100" 
                      @click.stop="verIncidencia(material)"
                      :disabled="loading"
                      style="display: block; padding: 0.5rem; font-weight: 500;">
                <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
              </button>
            </div>
            
            <div class="card-footer">
              <span class="text-muted">
                <i class="fas fa-check-circle text-success"></i>
                Entregado
              </span>
            </div>
          </div>
        </div>
      </div>


      <!-- Columna Revisar -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'revisar')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header revisar">
          <h5 class="mb-1">
            <i class="fas fa-search text-blue mr-2"></i>
            Revisar
          </h5>
          <div class="d-flex align-items-center justify-content-between" v-if="getMaterialesByEstado('revisar').length > 0">
            <span class="kanban-count revisar">{{ getMaterialesByEstado('revisar').length }}</span>
            <button 
              class="btn btn-sm btn-primary d-flex align-items-center justify-content-center btn-square"
              @click="abrirModalRevision"
              title="Revisión masiva">
              <i class="fas fa-qrcode"></i>
            </button>
          </div>
          <div v-else class="text-center">
            <span class="kanban-count revisar">{{ getMaterialesByEstado('revisar').length }}</span>
          </div>
        </div>
        <div class="column-content">
          <div v-for="material in getMaterialesByEstado('revisar')" 
            :key="material.id"
            class="material-card"
            :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
            draggable="true"
            @dragstart="onDragStart($event, material)"
            @dragend="onDragEnd"
            @click="handleCardClick($event, material)"
            
            style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
              <!-- Para Sacas -->
              <div v-if="material.es_saca || material.tipo_material === 'saca'">
                <h6 class="material-title mb-1">
                  <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                </h6>
                <p class="material-description mb-2">
                  <strong>QR:</strong> {{ material.qr || '-' }}
                </p>
              </div>
              <!-- Para Activos -->
              <div v-else-if="material.activo">
                <h6 class="material-title mb-1">
                  <strong>{{ material.activo.nombre }}</strong>
                </h6>
                <p v-if="material.activo.descripcion" class="material-description mb-2">
                  {{ material.activo.descripcion }}
                </p>
              </div>
              <!-- Para Consumibles -->
              <div v-else>
                <h6 class="material-title mb-1">
                  <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
              </h6>
                <p class="material-quantity mb-2">
                  <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                </p>
              </div>
              
              <!-- Información de Incidencia -->
              <div v-if="hasActiveIncidence(material)" 
                   class="incidence-info mt-2 p-2 bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded">
                <div class="d-flex align-items-center mb-1">
                  <i class="fas fa-exclamation-triangle text-warning me-2"></i>
                  <small class="text-warning fw-bold">INCIDENCIA</small>
                </div>
                <small class="text-muted d-block">
                  <strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}
                </small>
                <small class="text-muted d-block">
                  <strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}
                </small>
                <small class="text-muted d-block" v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                  <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 50) }}{{ material.incidencias[0].attributes.descripcion.length > 50 ? '...' : '' }}
                </small>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botones de Acción -->
              <div class="mt-3">
                <!-- Botón para ver incidencia (si existe) -->
                <button v-if="hasActiveIncidence(material)"
                        class="btn btn-warning btn-sm w-100 mb-2" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
                
                <!-- Botón de Aceptar Devolución -->
                <button class="btn btn-success btn-sm w-100" 
                        @click.stop="aceptarDevolucion(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-clipboard-check me-2"></i> Aceptar Devolución
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Devuelto -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'devuelto')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header devuelto">
          <h5 class="mb-0">
            <i class="fas fa-undo text-green mr-2"></i>
            Devuelto
          </h5>
          <span class="kanban-count devuelto">{{ getMaterialesByEstado('devuelto').length }}</span>
        </div>
        <div class="column-content">
          <div v-for="material in getMaterialesByEstado('devuelto')" 
            :key="material.id"
            class="material-card"
               :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
               draggable="true"
               @dragstart="onDragStart($event, material)"
               @dragend="onDragEnd"
               @click="handleCardClick($event, material)"
               
               style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <!-- Para Sacas -->
                  <div v-if="material.es_saca || material.tipo_material === 'saca'">
                    <h6 class="material-title mb-1">
                      <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                    </h6>
                    <p class="material-description mb-2">
                      <strong>QR:</strong> {{ material.qr || '-' }}
                    </p>
                  </div>
                  <!-- Para Activos -->
                  <div v-else-if="material.activo">
                    <h6 class="material-title mb-1">
                      <strong>{{ material.activo.nombre }}</strong>
                    </h6>
                    <p v-if="material.activo.descripcion" class="material-description mb-2">
                      {{ material.activo.descripcion }}
                    </p>
                  </div>
                  <!-- Para Consumibles -->
                  <div v-else>
                    <h6 class="material-title mb-1">
                      <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                    </h6>
                    <p class="material-quantity mb-2">
                      <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                    </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botón para ver incidencia (si existe) -->
              <div v-if="hasActiveIncidence(material)" class="mt-2">
                <button class="btn btn-incidence-enhanced btn-sm w-100" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
              </div>
            </div>
            <div class="card-footer">
              <span class="text-muted">
                <i class="fas fa-check-circle text-success"></i>
                Proceso completado
                </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Columna Finalizados/Especiales (Consumido, No Devuelto, Incidencia) -->
      <div class="kanban-column" 
           @drop="onDrop($event, 'finalizados')" 
           @dragover.prevent 
           @dragenter="onDragEnter($event)"
           @dragleave="onDragLeave($event)">
        <div class="column-header finalizados">
          <h5 class="mb-0">
            <i class="fas fa-flag text-warning mr-2"></i>
            Finalizados/Especiales
          </h5>
          <span class="kanban-count finalizados">{{ getMaterialesByEstado('finalizados').length }}</span>
        </div>
        <div class="column-content">
          <div v-for="material in getMaterialesByEstado('finalizados')" 
            :key="material.id"
            class="material-card"
            :class="[
              (material.es_saca || material.tipo_material === 'saca') ? 'material-card-saca' : 
              (material.activo ? 'material-card-activo' : 'material-card-consumible'),
              { 'material-card-con-incidencia': hasActiveIncidence(material) }
            ]"
            draggable="true"
            @dragstart="onDragStart($event, material)"
            @dragend="onDragEnd"
            @click="handleCardClick($event, material)"
            style="cursor: pointer;">
            <!-- Icono de alerta para incidencias -->
            <div v-if="hasActiveIncidence(material)" 
                 class="incidence-alert-icon">
              <i class="fas fa-exclamation-triangle"></i>
            </div>
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="flex-grow-1">
                  <!-- Para Sacas -->
                  <div v-if="material.es_saca || material.tipo_material === 'saca'">
                    <h6 class="material-title mb-1">
                      <strong><i class="fas fa-box text-primary mr-1"></i>{{ material.nombre || 'Saca sin nombre' }}</strong>
                    </h6>
                    <p class="material-description mb-2">
                      <strong>QR:</strong> {{ material.qr || '-' }}
                    </p>
                  </div>
                  <!-- Para Activos -->
                  <div v-else-if="material.activo">
                    <h6 class="material-title mb-1">
                      <strong>{{ material.activo.nombre }}</strong>
                    </h6>
                    <p v-if="material.activo.descripcion" class="material-description mb-2">
                      {{ material.activo.descripcion }}
                    </p>
                  </div>
                  <!-- Para Consumibles -->
                  <div v-else>
                    <h6 class="material-title mb-1">
                      <strong>{{ material.descripcion || material.idArticulo_flxtext }}</strong>
                    </h6>
                    <p class="material-quantity mb-2">
                      <strong>Cantidad:</strong> {{ material.cantidad }} {{ material.unidad || 'un' }}
                    </p>
                  </div>
                  
                  <!-- Badge de Incidencia Prominente -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-badge mt-2">
                    <i class="fas fa-exclamation-triangle"></i>
                    <span>INCIDENCIA ACTIVA</span>
                  </div>
                  
                  <!-- Información de Incidencia Mejorada -->
                  <div v-if="hasActiveIncidence(material)" 
                       class="incidence-info-enhanced mt-2">
                    <div class="incidence-title">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>DETALLES DE LA INCIDENCIA</span>
                    </div>
                    <div class="incidence-details">
                      <div><strong>Tipo:</strong> {{ material.incidencias && material.incidencias[0] ? getIncidenceType(material.incidencias[0]) : 'Sin tipo' }}</div>
                      <div><strong>Fecha:</strong> {{ material.incidencias && material.incidencias[0] ? formatDate(material.incidencias[0].attributes?.createdAt) : '' }}</div>
                      <div v-if="material.incidencias && material.incidencias[0] && material.incidencias[0].attributes?.descripcion">
                        <strong>Descripción:</strong> {{ material.incidencias[0].attributes.descripcion.substring(0, 60) }}{{ material.incidencias[0].attributes.descripcion.length > 60 ? '...' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
                <button class="btn btn-sm btn-outline-secondary edit-btn" 
                        @click.stop="editarMaterial(material)"
                        title="Editar material">
                  <i class="fas fa-edit"></i>
                </button>
              </div>
              
              <!-- Botón para ver incidencia (si existe) -->
              <div v-if="hasActiveIncidence(material)" class="mt-2">
                <button class="btn btn-incidence-enhanced btn-sm w-100" 
                        @click.stop="verIncidencia(material)"
                        :disabled="loading"
                        style="display: block; padding: 0.5rem; font-weight: 500;">
                  <i class="fas fa-exclamation-triangle me-2"></i> Ver Incidencia
                </button>
              </div>
            </div>
            <div class="card-footer">
              <span class="text-muted">
                <i v-if="material.estado === 'no_devuelto'" class="fas fa-ban text-warning"></i>
                <i v-else-if="material.estado === 'consumido'" class="fas fa-fire text-info"></i>
                <i v-else-if="material.estado === 'incidencia'" class="fas fa-exclamation-triangle text-danger"></i>
                {{ getEstadoTexto(material.estado) }}
                </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Revisión Masiva -->
    <div v-if="showRevisionModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-list-check text-primary mr-2"></i>
              Revisión de Devoluciones
            </h5>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="cerrarModalRevision" title="Cerrar">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <!-- Scanner QR -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">
                      <i class="fas fa-qrcode text-success mr-2"></i>
                      Escáner QR
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="input-group">
                      <input 
                        ref="qrScannerInput"
                        v-model="qrInput"
                        type="text" 
                        class="form-control" 
                        placeholder="Escanear QR del material o escribir código..."
                        @keyup.enter="procesarQR"
                        autocomplete="off"
                      >
                      <button class="btn btn-outline-secondary" type="button" @click="procesarQR">
                        <i class="fas fa-search"></i>
                      </button>
                    </div>
                    <small class="text-muted">
                      <i class="fas fa-info-circle mr-1"></i>
                      Escanea el QR del material o escribe el código y presiona Enter
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Lista de Materiales -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">
                      <i class="fas fa-list mr-2"></i>
                      Materiales Pendientes de Revisión ({{ materialesRevisar.length }})
              </h6>
                    <div>
                      <button 
                        class="btn btn-sm btn-success me-2"
                        @click="aceptarTodos"
                        :disabled="loading || materialesRevisar.length === 0">
                        <i class="fas fa-check-double"></i> Aceptar Todos
                      </button>
                      <button 
                        class="btn btn-sm btn-outline-secondary"
                        @click="cerrarModalRevision">
                        <i class="fas fa-times"></i> Cerrar
                      </button>
                    </div>
                  </div>
                  <div class="card-body p-0">
                    <div class="table-responsive">
                      <table class="table table-hover mb-0">
                        <thead class="table-light">
                          <tr>
                            <th width="5%" class="text-center">
                              <div class="form-check d-flex justify-content-center">
                                <input 
                                  type="checkbox" 
                                  v-model="seleccionarTodos"
                                  @change="toggleSeleccionTodos"
                                  class="form-check-input"
                                  id="selectAll"
                                >
                              </div>
                            </th>
                            <th width="10%">Tipo</th>
                            <th width="25%">Material</th>
                            <th width="15%">Código/QR</th>
                            <th width="10%">Cantidad</th>
                            <th width="15%">Estado</th>
                            <th width="20%">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="material in materialesRevisar" :key="material.id">
                            <td class="text-center">
                              <div class="form-check d-flex justify-content-center">
                                <input 
                                  type="checkbox" 
                                  v-model="materialesSeleccionados"
                                  :value="material.id"
                                  class="form-check-input"
                                  :id="'material-' + material.id"
                                >
                              </div>
                            </td>
                            <td>
                              <span class="badge text-white" 
                                    :class="(material.es_saca || material.tipo_material === 'saca') ? 'bg-primary' : 
                                            (material.activo ? 'bg-primary' : 'bg-purple')">
                                <i :class="(material.es_saca || material.tipo_material === 'saca') ? 'fas fa-box' : 
                                           (material.activo ? 'fas fa-cog' : 'fas fa-box')"></i>
                                {{ (material.es_saca || material.tipo_material === 'saca') ? 'Saca' : 
                                    (material.activo ? 'Activo' : 'Consumible') }}
                              </span>
                            </td>
                            <td>
                              <div>
                                <strong v-if="material.es_saca || material.tipo_material === 'saca'">
                                  {{ material.nombre || 'Saca sin nombre' }}
                                </strong>
                                <strong v-else-if="material.activo">
                                  {{ material.activo.nombre }}
                                </strong>
                                <strong v-else>
                                  {{ material.descripcion || material.idArticulo_flxtext }}
                                </strong>
                                <br>
                                <small class="text-muted" v-if="material.activo && material.activo.descripcion">
                                  {{ material.activo.descripcion }}
                                </small>
                                <small class="text-muted" v-else-if="material.es_saca || material.tipo_material === 'saca'">
                                  QR: {{ material.qr || '-' }}
                                </small>
                              </div>
                            </td>
                            <td>
                              <code v-if="material.es_saca || material.tipo_material === 'saca'">
                                {{ material.qr || '-' }}
                              </code>
                              <code v-else-if="material.activo">
                                {{ material.activo.numero_serie_qr }}
                              </code>
                              <code v-else>
                                {{ material.idArticulo }}
                              </code>
                            </td>
                            <td>
                              <span v-if="material.es_saca || material.tipo_material === 'saca'" class="text-muted">
                                -
                              </span>
                              <span v-else-if="!material.activo">
                                {{ material.cantidad }} {{ material.unidad || 'un' }}
                              </span>
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                              <span class="badge bg-warning text-white">
                                <i class="fas fa-clock"></i> Pendiente
                </span>
                            </td>
                            <td>
                              <button 
                                class="btn btn-sm btn-success me-1"
                                @click="aceptarMaterialIndividual(material)"
                                :disabled="loading">
                                <i class="fas fa-check"></i> Aceptar
                              </button>
                              <button 
                                class="btn btn-sm btn-outline-secondary"
                                @click="editarMaterial(material)">
                                <i class="fas fa-edit"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Cantidad para Consumibles -->
    <div v-if="showCantidadModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fas fa-box text-success mr-2"></i>
              Confirmar Cantidad - Consumible
            </h5>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="cerrarModalCantidad" title="Cerrar">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label"><strong>Material:</strong></label>
              <p class="mb-0">{{ materialCantidadSeleccionado?.descripcion || materialCantidadSeleccionado?.idArticulo_flxtext }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label"><strong>Cantidad Original:</strong></label>
              <p class="mb-0">{{ materialCantidadSeleccionado?.cantidad }} {{ materialCantidadSeleccionado?.unidad || 'un' }}</p>
            </div>
            <div class="mb-3">
              <label for="cantidadDevolucion" class="form-label"><strong>Cantidad a Devolver:</strong></label>
              <input 
                id="cantidadDevolucion"
                v-model="cantidadDevolucion"
                type="number" 
                class="form-control" 
                :placeholder="materialCantidadSeleccionado?.cantidad"
                :max="materialCantidadSeleccionado?.cantidad"
                min="1"
                @keyup.enter="confirmarCantidad"
              >
              <small class="text-muted">
                Máximo: {{ materialCantidadSeleccionado?.cantidad }} {{ materialCantidadSeleccionado?.unidad || 'un' }}
              </small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModalCantidad">
              <i class="fas fa-times"></i> Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-success"
              @click="confirmarCantidad"
              :disabled="!cantidadDevolucion || cantidadDevolucion <= 0">
              <i class="fas fa-check"></i> Confirmar Devolución
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Incidencia -->
    <div v-if="showIncidenciaModal" class="modal fade show" style="display: block;" tabindex="-1" aria-labelledby="modalIncidenciaLabel" aria-hidden="false">
      <div class="modal-backdrop fade show" @click="cerrarModalIncidencia"></div>
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="modalIncidenciaLabel">
              <i class="fas fa-exclamation-triangle text-warning me-2"></i>
              Detalles de la Incidencia
            </h5>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="cerrarModalIncidencia" title="Cerrar">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="modal-body" v-if="incidenciaSeleccionada">
            <!-- Información del Material -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card bg-light">
                  <div class="card-body py-2">
                    <h5 class="card-title">
                      <i class="fas fa-box me-2"></i>Material Afectado
                    </h5>
                    <div class="row">
                      <div class="col-md-6">
                        <strong>Nombre:</strong> 
                        {{ incidenciaSeleccionada.attributes?.activo?.data?.attributes?.nombre || 'Sin nombre' }}
                      </div>
                      <div class="col-md-6">
                        <strong>ID:</strong> {{ incidenciaSeleccionada.id }}
                      </div>
                      <div class="col-md-6">
                        <strong>Número de Serie:</strong> {{ incidenciaSeleccionada.attributes?.activo?.data?.attributes?.numero_serie_qr || 'Sin número de serie' }}
                      </div>
                      <div class="col-md-6">
                        <strong>Ubicación:</strong> {{ incidenciaSeleccionada.attributes?.activo?.data?.attributes?.ubicacion || 'Sin ubicación' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Información de la Incidencia -->
            <div class="row mb-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-body py-2">
                    <h5 class="card-title">
                      <i class="fas fa-exclamation-triangle text-warning me-2"></i>Información de la Incidencia
                    </h5>
                    <div class="row">
                      <div class="col-md-6">
                        <strong>Tipo de Incidencia:</strong> 
                        {{ incidenciaSeleccionada.attributes?.tipo_incidencia?.data?.attributes?.descripcion || 'Sin tipo' }}
                      </div>
                      <div class="col-md-6">
                        <strong>Estado:</strong> 
                        <span class="badge" :class="getEstadoBadgeClass(incidenciaSeleccionada.attributes?.estado_incidencia?.data?.attributes?.nombre)">
                          {{ incidenciaSeleccionada.attributes?.estado_incidencia?.data?.attributes?.nombre || 'Sin estado' }}
                        </span>
                      </div>
                      <div class="col-md-6">
                        <strong>Fecha de Creación:</strong> 
                        {{ formatDate(incidenciaSeleccionada.attributes?.createdAt) }}
                      </div>
                      <div class="col-md-6" v-if="incidenciaSeleccionada.attributes?.updatedAt !== incidenciaSeleccionada.attributes?.createdAt">
                        <strong>Última Actualización:</strong> 
                        {{ formatDate(incidenciaSeleccionada.attributes?.updatedAt) }}
                      </div>
                    </div>
                    <div class="row mt-3" v-if="incidenciaSeleccionada.attributes?.descripcion">
                      <div class="col-12">
                        <strong>Descripción:</strong>
                        <p class="mt-1">{{ incidenciaSeleccionada.attributes.descripcion }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Resolución de la Incidencia -->
            <div class="row" v-if="incidenciaSeleccionada.estado_incidencia?.nombre !== 'Resuelta'">
              <div class="col-12">
                <div class="card border-success">
                  <div class="card-body py-2">
                    <h5 class="card-title text-success">
                      <i class="fas fa-check-circle me-2"></i>Resolver Incidencia
                    </h5>
                    <div class="mb-2">
                      <label for="comentarioResolucion" class="form-label">
                        <strong>Comentario de Resolución:</strong>
                      </label>
                      <textarea 
                        id="comentarioResolucion"
                        v-model="comentarioResolucion"
                        class="form-control" 
                        rows="2"
                        placeholder="Describe cómo se resolvió la incidencia..."
                        required>
                      </textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Historial de la Incidencia (si está resuelta) -->
            <div class="row" v-else>
              <div class="col-12">
                <div class="card border-success">
                  <div class="card-body">
                    <h6 class="card-title text-success">
                      <i class="fas fa-check-circle me-2"></i>Incidencia Resuelta
                    </h6>
                    <p class="text-success mb-0">
                      <i class="fas fa-info-circle me-2"></i>
                      Esta incidencia ya ha sido resuelta.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModalIncidencia">
              <i class="fas fa-times"></i> Cerrar
            </button>
            <button 
              v-if="incidenciaSeleccionada && incidenciaSeleccionada.estado_incidencia?.nombre !== 'Resuelta'"
              type="button" 
              class="btn btn-success"
              @click="resolverIncidencia"
              :disabled="!comentarioResolucion || loading">
              <i class="fas fa-check"></i> Resolver Incidencia
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getEstadoTexto } from '@/util/estadoColors.js';
import axios from 'axios';

export default {
  name: 'MaterialKanban',
  props: {
    materiales: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    albaran: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      draggedMaterial: null,
      // Variables para el modal de revisión
      showRevisionModal: false,
      qrInput: '',
      materialesSeleccionados: [],
      seleccionarTodos: false,
      // Variables para el modal de cantidad
      showCantidadModal: false,
      materialCantidadSeleccionado: null,
      cantidadDevolucion: null,
      // Variables para el modal de incidencia
      showIncidenciaModal: false,
      incidenciaSeleccionada: null,
      comentarioResolucion: ''
    };
  },
  watch: {
    albaran: {
      handler(newAlbaran) {
        // El albarán ha cambiado, los materiales se actualizarán automáticamente
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    hasJefeEquipoAsignado() {
      return this.albaran && this.albaran.users_permissions_user;
    },
    
    materialesRevisar() {
      return this.getMaterialesByEstado('revisar');
    }
  },
  methods: {
    getEstadoTexto,
    
    getMaterialesByEstado(estado) {
      switch (estado) {
        case 'preparacion':
          // Solo materiales sin jefe de equipo asignado al albarán
          if (!this.hasJefeEquipoAsignado) {
            return this.materiales.filter(m => 
              !m.responsable_actual || m.responsable_actual === 'jefe_taller'
            );
          }
          // Si ya hay jefe de equipo asignado al albarán, no debe haber materiales en preparación
          return [];
        case 'asignado':
          // Materiales con jefe de equipo asignado al albarán pero pendientes de aceptar
          return this.materiales.filter(m => 
            this.hasJefeEquipoAsignado && 
            (m.estado === 'pendiente_aceptar' || !m.estado)
          );
        case 'entregado':
          // Materiales aceptados por el jefe de equipo (estado 'entregado')
          return this.materiales.filter(m => 
            m.estado === 'entregado' || m.estado === 'asignado' // Mantener compatibilidad con datos antiguos
          );
        case 'rechazado':
          return this.materiales.filter(m => 
            m.estado === 'rechazado'
          );
        case 'revisar':
          return this.materiales.filter(m => 
            m.estado === 'pendiente_aceptar_devolucion'
          );
        case 'devuelto':
          return this.materiales.filter(m => 
            m.estado === 'devuelto'
          );
        case 'finalizados':
          // Materiales en estados finalizados o especiales: consumido, no_devuelto, incidencia
          return this.materiales.filter(m => 
            m.estado === 'consumido' || 
            m.estado === 'no_devuelto' || 
            m.estado === 'incidencia'
          );
        default:
          return [];
      }
    },

    onDragStart(event, material) {
      this.draggedMaterial = material;
      event.dataTransfer.effectAllowed = 'move';
      // Añadir clase visual al card que se está arrastrando
      event.target.classList.add('dragging');
    },

    onDragEnd(event) {
      // Remover clase visual
      event.target.classList.remove('dragging');
      // Remover clase de todas las columnas
      document.querySelectorAll('.kanban-column').forEach(col => {
        col.classList.remove('drag-over');
      });
      // Limpiar la variable después de un tiempo para permitir clicks
      setTimeout(() => {
        this.draggedMaterial = null;
      }, 100);
    },
    
    onDragEnter(event) {
      event.preventDefault();
      // Solo añadir clase si es la columna misma (no un hijo)
      if (event.currentTarget.classList.contains('kanban-column')) {
        event.currentTarget.classList.add('drag-over');
      }
    },

    onDragLeave(event) {
      // Solo remover clase si salimos de la columna (no de un hijo)
      // Verificar que el relatedTarget no sea un hijo de la columna
      if (event.currentTarget.classList.contains('kanban-column') && 
          !event.currentTarget.contains(event.relatedTarget)) {
        event.currentTarget.classList.remove('drag-over');
      }
    },

    onDrop(event, targetEstado) {
      event.preventDefault();
      
      // Remover clase visual de la columna
      event.currentTarget.classList.remove('drag-over');
      
      if (!this.draggedMaterial) return;
      
      const material = this.draggedMaterial;
      const currentEstado = this.getMaterialEstado(material);
      
      // Solo permitir cambios de estado válidos
      if (this.isValidStateTransition(currentEstado, targetEstado)) {
        this.$emit('change-material-state', {
          material,
          newEstado: this.getTargetEstado(targetEstado)
        });
      } else {
        this.$notify({
          type: 'warning',
          message: `No se puede mover el material de "${this.getEstadoTexto(currentEstado)}" a "${this.getEstadoTexto(targetEstado)}"`
        });
      }
      
      this.draggedMaterial = null;
    },

    getMaterialEstado(material) {
      // Preparación: solo si NO hay jefe de equipo asignado al albarán
      if (!this.hasJefeEquipoAsignado && (!material.responsable_actual || material.responsable_actual === 'jefe_taller')) {
        return 'preparacion';
      }
      // Asignado: con jefe de equipo asignado al albarán pero no aceptado
      else if (this.hasJefeEquipoAsignado && 
               (material.estado === 'pendiente_aceptar' || !material.estado)) {
        return 'asignado';
      }
      // Entregado: aceptado por jefe de equipo (estado 'entregado' o 'asignado' para compatibilidad)
      else if (material.estado === 'entregado' || material.estado === 'asignado') {
        return 'entregado';
      }
      // Rechazado
      else if (material.estado === 'rechazado') {
        return 'rechazado';
      }
      // Revisar: pendiente de devolución
      else if (material.estado === 'pendiente_aceptar_devolucion') {
        return 'revisar';
      }
      // Devuelto
      else if (material.estado === 'devuelto') {
        return 'devuelto';
      }
      // Finalizados/Especiales: consumido, no_devuelto, incidencia
      else if (material.estado === 'consumido' || 
               material.estado === 'no_devuelto' || 
               material.estado === 'incidencia') {
        return 'finalizados';
      }
      return 'preparacion';
    },

    getTargetEstado(kanbanEstado) {
      switch (kanbanEstado) {
        case 'preparacion':
          return 'pendiente_aceptar';
        case 'asignado':
          return 'pendiente_aceptar'; // La columna "asignado" del kanban es para materiales pendientes
        case 'entregado':
          return 'entregado'; // La columna "entregado" del kanban usa estado "entregado" en el material
        case 'rechazado':
          return 'rechazado';
        case 'revisar':
          return 'pendiente_aceptar_devolucion';
        case 'devuelto':
          return 'devuelto';
        case 'finalizados':
          // No se permite cambiar a finalizados mediante drag and drop
          // Estos estados se asignan manualmente desde la edición
          // Retornar el estado actual del material si está disponible, sino pendiente_aceptar
          return 'pendiente_aceptar'; // Por defecto, aunque no debería usarse
        default:
          return 'pendiente_aceptar';
      }
    },

    isValidStateTransition(currentEstado, targetEstado) {
      // Si el estado origen y destino son iguales, no permitir
      if (currentEstado === targetEstado) {
        return false;
      }

      // No permitir mover a "finalizados" mediante drag and drop
      // Estos estados (consumido, no_devuelto, incidencia) se asignan manualmente
      if (targetEstado === 'finalizados') {
        return false;
      }

      // Permitir todas las demás transiciones entre estados del kanban
      // Esto permite flexibilidad para reorganizar materiales según necesidades
      const allowedStates = ['preparacion', 'asignado', 'entregado', 'rechazado', 'revisar', 'devuelto'];
      
      return allowedStates.includes(currentEstado) && allowedStates.includes(targetEstado);
    },

    asignarJefeEquipo(material) {
      this.$emit('asignar-jefe-equipo', material);
    },

    aceptarMaterial(material) {
      this.$emit('aceptar-material', material);
    },

    rechazarMaterial(material) {
      this.$emit('rechazar-material', material);
    },

    solicitarDevolucion(material) {
      this.$emit('solicitar-devolucion', material);
    },

    aceptarDevolucion(material) {
      this.$emit('aceptar-devolucion', material);
    },

    rechazarDevolucion(material) {
      this.$emit('rechazar-devolucion', material);
    },

    marcarComoNoDevuelto(material) {
      this.$emit('marcar-no-devuelto', material);
    },

    marcarComoConsumido(material) {
      this.$emit('marcar-consumido', material);
    },
    
    editarMaterial(material) {
      this.$emit('editar-material', material);
    },

    // Métodos para el modal de revisión
    abrirModalRevision() {
      this.showRevisionModal = true;
      this.materialesSeleccionados = [];
      this.seleccionarTodos = false;
      this.qrInput = '';
      this.$nextTick(() => {
        if (this.$refs.qrScannerInput) {
          this.$refs.qrScannerInput.focus();
        }
      });
    },

    cerrarModalRevision() {
      this.showRevisionModal = false;
      this.materialesSeleccionados = [];
      this.seleccionarTodos = false;
      this.qrInput = '';
    },

    toggleSeleccionTodos() {
      if (this.seleccionarTodos) {
        this.materialesSeleccionados = this.materialesRevisar.map(m => m.id);
      } else {
        this.materialesSeleccionados = [];
      }
    },

    procesarQR() {
      if (!this.qrInput.trim()) return;

      // Extraer código QR de URL si es necesario
      let codigo = this.qrInput.trim();
      
      // Si es una URL de misepis.com/code/, extraer el código
      if (codigo.includes('misepis.com/code/')) {
        codigo = codigo.split('code/').pop().split('/')[0];
        this.qrInput = codigo; // Actualizar el input con solo el código
      } else if (codigo.includes('ssea.es/')) {
        // Si es una URL de ssea.es/, extraer el código después de ssea.es/
        codigo = codigo.split('ssea.es/').pop().split('/')[0];
        this.qrInput = codigo; // Actualizar el input con solo el código
      } else if ((codigo.startsWith('http://') || codigo.startsWith('https://')) && codigo.split('/').length > 3) {
        // Manejar otras URLs (con http/https pero que no sean misepis.com/code/ ni ssea.es/)
        const lastSegment = codigo.split('/').pop();
        if (lastSegment) {
          codigo = lastSegment;
          this.qrInput = codigo; // Actualizar el input con solo el código
        }
      }
      // Si no es URL, usar el código tal cual sin modificar
      
      const material = this.materialesRevisar.find(m => {
        if (m.es_saca || m.tipo_material === 'saca') {
          return m.qr === codigo;
        } else if (m.activo) {
          return m.activo.numero_serie_qr === codigo || m.activo.numero_serie === codigo;
        } else {
          return m.idArticulo === codigo;
        }
      });

      if (material) {
        if (material.es_saca || material.tipo_material === 'saca') {
          // Saca: aceptar directamente (se trata como activo)
          this.aceptarMaterialIndividual(material);
        } else if (material.activo) {
          // Activo: aceptar directamente
          this.aceptarMaterialIndividual(material);
        } else {
          // Consumible: abrir modal de cantidad
          this.materialCantidadSeleccionado = material;
          this.cantidadDevolucion = material.cantidad;
          this.showCantidadModal = true;
        }
        this.qrInput = '';
      } else {
        this.$notify({
          type: 'warning',
          message: `No se encontró material con código: ${codigo}`
        });
        this.qrInput = '';
      }
    },

    aceptarMaterialIndividual(material) {
      if (material.es_saca || material.tipo_material === 'saca') {
        // Saca: aceptar directamente (se trata como activo)
        this.$emit('aceptar-devolucion', material);
      } else if (material.activo) {
        // Activo: aceptar directamente
        this.$emit('aceptar-devolucion', material);
      } else {
        // Consumible: abrir modal de cantidad
        this.materialCantidadSeleccionado = material;
        this.cantidadDevolucion = material.cantidad;
        this.showCantidadModal = true;
      }
    },

    aceptarTodos() {
      const materialesSeleccionados = this.materialesRevisar.filter(m => 
        this.materialesSeleccionados.includes(m.id)
      );
      
      if (materialesSeleccionados.length === 0) {
        this.$notify({
          type: 'warning',
          message: 'Selecciona al menos un material para aceptar'
        });
        return;
      }

      // Emitir evento para aceptar múltiples materiales
      this.$emit('aceptar-devoluciones-masivas', materialesSeleccionados);
    },

    // Métodos para el modal de cantidad
    cerrarModalCantidad() {
      this.showCantidadModal = false;
      this.materialCantidadSeleccionado = null;
      this.cantidadDevolucion = null;
    },

    confirmarCantidad() {
      if (!this.cantidadDevolucion || this.cantidadDevolucion <= 0) {
        this.$notify({
          type: 'warning',
          message: 'Ingresa una cantidad válida'
        });
        return;
      }

      if (this.cantidadDevolucion > this.materialCantidadSeleccionado.cantidad) {
        this.$notify({
          type: 'warning',
          message: 'La cantidad no puede ser mayor a la original'
        });
        return;
      }

      // Emitir evento con la cantidad específica
      this.$emit('aceptar-devolucion-consumible', {
        material: this.materialCantidadSeleccionado,
        cantidad: this.cantidadDevolucion
      });

      this.cerrarModalCantidad();
    },

    // Métodos para el modal de incidencia
    hasActiveIncidence(material) {
      if (!material.incidencias || !Array.isArray(material.incidencias) || material.incidencias.length === 0) {
        return false;
      }
      
      // Usar la estructura correcta de Strapi v4
      const tieneIncidenciaActiva = material.incidencias.some(inc => {
        if (!inc) return false;
        
        // Intentar obtener el estado desde diferentes estructuras posibles
        const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre ||
                            inc.estado_incidencia?.data?.attributes?.nombre ||
                            inc.estado_incidencia?.nombre ||
                            inc.attributes?.estado_incidencia?.nombre;
        
        // Si no tiene estado definido, NO asumir que está activa (más conservador)
        if (!estadoNombre) {
          console.warn('⚠️ Incidencia sin estado definido detectada:', inc);
          return false; // Cambiado: si no hay estado, NO se considera activa
        }
        
        // Solo considerar activas las incidencias que NO estén resueltas ni cerradas
        const estadoLower = estadoNombre.toLowerCase();
        // Excluir incidencias resueltas y cerradas
        if (estadoLower === 'resuelta' || estadoLower === 'cerrada') {
          return false;
        }
        // Si tiene estado y no es resuelta ni cerrada, está activa
        return true;
      });
      
      // Log para depuración
      if (tieneIncidenciaActiva) {
        console.log('🔍 Material con incidencia activa detectada:', {
          materialId: material.id,
          activoId: material.activo?.id,
          activoNombre: material.activo?.nombre,
          incidencias: material.incidencias.map(inc => ({
            id: inc.id || inc.data?.id,
            estado: inc.attributes?.estado_incidencia?.data?.attributes?.nombre ||
                   inc.estado_incidencia?.data?.attributes?.nombre ||
                   inc.estado_incidencia?.nombre ||
                   inc.attributes?.estado_incidencia?.nombre || 'sin estado'
          }))
        });
      }
      
      return tieneIncidenciaActiva;
    },
    
    getFirstActiveIncidence(material) {
      if (!material.incidencias || !Array.isArray(material.incidencias) || material.incidencias.length === 0) {
        return null;
      }
      
      return material.incidencias.find(inc => {
        if (!inc) return false;
        
        // Estructura Strapi v4: inc.attributes.estado_incidencia.data.attributes.nombre
        const estadoNombre = inc.attributes?.estado_incidencia?.data?.attributes?.nombre;
        
        // Solo considerar activas las incidencias que NO estén resueltas ni cerradas
        if (estadoNombre) {
          const estadoLower = estadoNombre.toLowerCase();
          // Excluir incidencias resueltas y cerradas
          if (estadoLower === 'resuelta' || estadoLower === 'cerrada') {
            return false;
          }
          // Si tiene estado y no es resuelta ni cerrada, está activa
          return true;
        }
        
        // Fallback: si no tiene estado definido, asumir que está activa
        return true;
      }) || null;
    },
    
    getIncidenceType(incidence) {
      if (!incidence) return 'Sin tipo';
      
      // Estructura Strapi v4: incidence.attributes.tipo_incidencia.data.attributes.descripcion
      const tipoDescripcion = incidence.attributes?.tipo_incidencia?.data?.attributes?.descripcion;
      if (tipoDescripcion) {
        return tipoDescripcion;
      }
      
      return 'Sin tipo';
    },
    
    extraerMotivoRechazo(observaciones) {
      if (!observaciones) return '';
      
      // Buscar el patrón "MATERIAL RECHAZADO - Motivo: ..." o "DEVOLUCIÓN RECHAZADA - Motivo: ..."
      const match = observaciones.match(/(?:MATERIAL RECHAZADO|DEVOLUCIÓN RECHAZADA)\s*-\s*Motivo:\s*(.+?)(?:\n|$)/i);
      if (match && match[1]) {
        return match[1].trim();
      }
      
      // Si no encuentra el patrón, devolver las primeras 80 caracteres
      return observaciones.length > 80 ? observaciones.substring(0, 80) + '...' : observaciones;
    },

    getEstadoBadgeClass(estado) {
      if (!estado) return 'badge-secondary text-white';
      
      switch (estado.toLowerCase()) {
        case 'abierta':
          return 'badge-warning text-white';
        case 'en proceso':
          return 'badge-info text-white';
        case 'resuelta':
          return 'badge-success text-white';
        case 'cerrada':
          return 'badge-secondary text-white';
        default:
          return 'badge-secondary text-white';
      }
    },
    
    verIncidencia(material) {
      this.incidenciaSeleccionada = this.getFirstActiveIncidence(material);
      console.log('🔍 Material:', material);
      console.log('🔍 Incidencia seleccionada:', this.incidenciaSeleccionada);
      this.comentarioResolucion = '';
      this.showIncidenciaModal = true;
    },

    cerrarModalIncidencia() {
      this.showIncidenciaModal = false;
      this.incidenciaSeleccionada = null;
      this.comentarioResolucion = '';
    },

    async resolverIncidencia() {
      if (!this.comentarioResolucion.trim()) {
        this.$notify({
          type: 'warning',
          message: 'Debes agregar un comentario de resolución'
        });
        return;
      }

      if (!this.incidenciaSeleccionada || !this.incidenciaSeleccionada.id) {
        this.$notify({
          type: 'error',
          message: 'No se puede identificar la incidencia a resolver'
        });
        return;
      }

      try {
        this.loading = true;
        
        // Obtener el ID de la incidencia (puede estar en .id o .attributes.id)
        const incidenciaId = this.incidenciaSeleccionada.id || 
                            this.incidenciaSeleccionada.attributes?.id ||
                            this.incidenciaSeleccionada.data?.id;
        
        // Construir el objeto de incidencia en el formato correcto para Strapi
        // El servicio envuelve esto en { data: ... }
        const incidenciaUpdate = {
          id: incidenciaId,
          resolucion: this.comentarioResolucion.trim(),
          estado_incidencia: {
            id: 2 // ID del estado "Resuelta"
          },
          fecha_resolucion: new Date().toISOString() // Fecha actual en formato ISO
        };
        
        // Si la incidencia tiene documentos relacionados, mantenerlos
        // En formato Strapi v4: attributes.incidence_documents.data[]
        // En formato deserializado: incidence_documents: [id, id, ...]
        if (this.incidenciaSeleccionada.attributes?.incidence_documents?.data) {
          // Mantener los IDs de los documentos
          incidenciaUpdate.incidence_documents = this.incidenciaSeleccionada.attributes.incidence_documents.data.map(doc => doc.id);
        } else if (this.incidenciaSeleccionada.incidence_documents) {
          // Si ya está en formato deserializado
          incidenciaUpdate.incidence_documents = Array.isArray(this.incidenciaSeleccionada.incidence_documents) 
            ? this.incidenciaSeleccionada.incidence_documents.map(doc => doc.id || doc)
            : this.incidenciaSeleccionada.incidence_documents;
        }
        
        console.log('Actualizando incidencia con formato:', incidenciaUpdate);
        
        // Usar el store Vuex como en ViewIncidenciaPage.vue para mantener consistencia
        await this.$store.dispatch("incidencias/update", incidenciaUpdate);

        // Actualizar el estado de la incidencia localmente para reflejar el cambio inmediatamente
        if (this.incidenciaSeleccionada.attributes) {
          // Actualizar el estado de la incidencia en el objeto local
          if (this.incidenciaSeleccionada.attributes.estado_incidencia?.data) {
            this.incidenciaSeleccionada.attributes.estado_incidencia.data.id = 2;
            this.incidenciaSeleccionada.attributes.estado_incidencia.data.attributes = {
              ...this.incidenciaSeleccionada.attributes.estado_incidencia.data.attributes,
              nombre: 'Resuelta'
            };
          }
          // Actualizar la resolución
          this.incidenciaSeleccionada.attributes.resolucion = this.comentarioResolucion.trim();
        } else {
          // Si está en formato deserializado
          if (this.incidenciaSeleccionada.estado_incidencia) {
            this.incidenciaSeleccionada.estado_incidencia.id = 2;
          }
          this.incidenciaSeleccionada.resolucion = this.comentarioResolucion.trim();
        }

        this.$notify({
          type: 'success',
          message: 'Incidencia resuelta correctamente'
        });

        // Emitir evento para actualizar los datos en el componente padre
        this.$emit('incidencia-resuelta', {
          incidencia: this.incidenciaSeleccionada,
          comentario: this.comentarioResolucion
        });

        this.cerrarModalIncidencia();
        
      } catch (error) {
        console.error('Error al resolver la incidencia:', error);
        console.error('Detalles del error:', error.response?.data);
        this.$notify({
          type: 'error',
          message: `Error al resolver la incidencia: ${error.response?.data?.error?.message || error.message || 'Error desconocido'}`
        });
      } finally {
        this.loading = false;
      }
    },

    // Manejo del click en el card
    handleCardClick(event, material) {
      console.log('🖱️ CLICK DETECTADO en card!');
      console.log('🖱️ Material:', material);
      console.log('🖱️ Evento detail:', event.detail);
      console.log('🖱️ Dragged material:', this.draggedMaterial);
      
      // Prevenir el comportamiento por defecto
      event.preventDefault();
      event.stopPropagation();
      
      // Verificar si es un click simple (no drag)
      if (event.detail === 1 && !this.draggedMaterial) {
        console.log('🖱️ Ejecutando navegación...');
        this.irADetalleActivo(material);
      } else {
        console.log('🖱️ Click ignorado - posible drag o múltiple click');
      }
    },

    // Navegación al detalle del activo
    irADetalleActivo(material) {
      console.log('🔍 irADetalleActivo llamado con material:', material);
      
      if (material.es_saca || material.tipo_material === 'saca') {
        console.log('📦 Mostrando información de Saca');
        // Para Sacas, mostrar información
        this.$notify({
          type: 'info',
          message: `Saca: ${material.nombre || 'Saca sin nombre'} (QR: ${material.qr || '-'})`
        });
      } else if (material.activo) {
        console.log('📦 Navegando a detalle de activo:', material.activo.id);
        // Para activos, navegar al detalle del activo
        this.$router.push({
          name: 'View Activo',
          params: { id: material.activo.id }
        }).catch(error => {
          console.error('❌ Error navegando al detalle del activo:', error);
          this.$notify({
            type: 'error',
            message: 'Error al navegar al detalle del activo'
          });
        });
      } else {
        console.log('📦 Mostrando información de consumible');
        // Para consumibles, mostrar información o navegar a una página de detalle de consumible
        this.$notify({
          type: 'info',
          message: `Consumible: ${material.descripcion || material.idArticulo_flxtext}`
        });
      }
    },

    // Método para formatear fechas
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.material-kanban {
  padding: 1rem;
}

.kanban-container {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  min-height: 600px;
}

.kanban-column {
  flex: 1;
  min-width: 280px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;
}

.column-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 8px 8px 0 0;
  z-index: 10;
}

.column-header.preparacion {
  background: white;
}

.column-header.asignado {
  background: white;
}

.column-header.entregado {
  background: white;
}

/* Estilos para materiales rechazados dentro de la columna asignado */
.material-card-rechazado {
  border-left: 4px solid #dc3545;
  background: #fff5f5;
}

.material-card-rechazado .card-header {
  background: #f8d7da;
}

.material-card-rechazado .card-footer {
  background: #f8d7da;
}

.column-header.revisar {
  background: white;
}

.column-header.devuelto {
  background: white;
}

.column-header.finalizados {
  background: white;
}

/* Estilos para los contadores de Kanban */
.kanban-count {
  font-size: 1.5rem;
  font-weight: bold;
  background: none;
  border: none;
  padding: 0;
}

.kanban-count.preparacion {
  color: #fd7e14;
}

.kanban-count.asignado {
  color: #007bff;
}

.kanban-count.entregado {
  color: #ffc107;
}

.kanban-count.revisar {
  color: #007bff;
}

.kanban-count.devuelto {
  color: #28a745;
}

.kanban-count.finalizados {
  color: #ffc107;
}

/* Clases de color personalizadas para iconos */
.text-orange {
  color: #fd7e14 !important;
}

.text-blue {
  color: #007bff !important;
}

.text-green {
  color: #28a745 !important;
}

.column-content {
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.material-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.material-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

.material-card:active {
  transform: rotate(5deg);
}

.card-header {
  padding: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
  background: #fafafa;
}

.card-body {
  padding: 0.75rem;
}

.card-footer {
  padding: 0.75rem;
  border-top: 1px solid #f1f3f4;
  background: #fafafa;
}

.material-type {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
}

.material-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.material-details {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.material-description {
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
}

.material-quantity {
  font-size: 0.8rem;
  color: #495057;
}

/* Estilos para diferenciar activos y consumibles */
.material-card-activo {
  border-left: 4px solid #007bff;
}

.material-card-consumible {
  border-left: 4px solid #6f42c1;
}

.material-card-saca {
  border-left: 4px solid #007bff;
  background: #f0f7ff;
}

.material-card-rechazado.material-card-activo {
  border-left: 4px solid #dc3545;
}

.material-card-rechazado.material-card-consumible {
  border-left: 4px solid #dc3545;
}

/* Estilos para el botón de editar */
.edit-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.edit-btn:hover {
  opacity: 1;
}

/* Estilos para los checkboxes del modal */
.modal .form-check-input {
  width: 1.25rem;
  height: 1.25rem;
  margin: 0;
  border: 2px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #fff;
  cursor: pointer;
  vertical-align: middle;
}

.modal .form-check-input:checked {
  background-color: #007bff;
  border-color: #007bff;
}

.modal .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

/* Asegurar que los checkboxes sean visibles y centrados */
.modal .table td:first-child,
.modal .table th:first-child {
  text-align: center;
  vertical-align: middle;
  width: 50px;
  padding: 0.75rem 0.5rem;
}

.modal .form-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
}

/* Estilos para badges personalizados */
.bg-purple {
  background-color: #6f42c1 !important;
}

.bg-purple:hover {
  background-color: #5a32a3 !important;
}

/* Estilos para botones de cerrar del modal */
.modal-header .btn-outline-secondary {
  border: 1px solid #dee2e6;
  color: #6c757d;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.modal-header .btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
  transform: translateY(-1px);
}

.badge-sm {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

/* Drag and drop visual feedback */
.kanban-column.drag-over {
  background: #e3f2fd !important;
  border-color: #2196f3 !important;
  border-width: 2px !important;
  box-shadow: 0 0 10px rgba(33, 150, 243, 0.3) !important;
}

.material-card.dragging {
  opacity: 0.5;
  transform: rotate(2deg) scale(0.95);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
  cursor: grabbing !important;
}

.material-card:hover:not(.dragging) {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-2px);
}

/* Responsive design */
@media (max-width: 768px) {
  .kanban-container {
    flex-direction: column;
  }
  
  .kanban-column {
    min-width: 100%;
  }
}

/* Scrollbar styling */
.kanban-column::-webkit-scrollbar {
  width: 6px;
}

.kanban-column::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.kanban-column::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.kanban-column::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* ===== ESTILOS PARA INCIDENCIAS ===== */

/* Card con incidencia - diseño más sutil */
.material-card-con-incidencia {
  border: 2px solid #e74c3c !important;
  border-radius: 8px !important;
  position: relative;
  background: #fefefe !important;
  box-shadow: 0 2px 8px rgba(231, 76, 60, 0.15) !important;
}


/* Icono de alerta en la esquina superior derecha - más sutil */
.incidence-alert-icon {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}


/* Badge de incidencia - más sutil */
.incidence-badge {
  background: #e74c3c;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin: 6px 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}


/* Información de incidencia - más sutil */
.incidence-info-enhanced {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-left: 3px solid #e74c3c;
  border-radius: 4px;
  padding: 10px;
  margin: 6px 0;
}

/* Título de incidencia - más sutil */
.incidence-title {
  color: #495057;
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.incidence-title i {
  font-size: 12px;
}


/* Detalles de incidencia */
.incidence-details {
  font-size: 11px;
  color: #6c757d;
  line-height: 1.4;
}

.incidence-details strong {
  color: #495057;
  font-weight: 600;
}

/* Botón de ver incidencia mejorado */
.btn-incidence-enhanced {
  background: #e74c3c;
  border: 1px solid #e74c3c;
  color: white;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-incidence-enhanced:hover {
  background: #c0392b;
  border-color: #c0392b;
  transform: none;
}

/* Hover sutil para cards con incidencia */
.material-card-con-incidencia:hover {
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.2) !important;
}

/* Estilos para el modal de incidencia */
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  position: relative;
  z-index: 1050;
  margin: 1.75rem auto;
  max-width: 500px;
}

.modal-lg {
  max-width: 800px;
}

.modal-xl {
  max-width: 1200px;
}

/* Estilos para hacer el modal más compacto */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
  font-size: 0.9rem;
}

.modal-dialog {
  max-height: 90vh;
  margin: 1rem auto;
}

.modal-content {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-footer {
  margin-top: auto;
  flex-shrink: 0;
}

/* Texto más pequeño en el modal */
.modal-body .card-title {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.modal-body .form-label {
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
}

.modal-body .form-control {
  font-size: 0.85rem;
}

.modal-body strong {
  font-size: 0.85rem;
}

/* Botón cuadrado para revisión */
.btn-square {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 4px;
}
</style>
